import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  ToggleButton,
  Fade,
  alpha,
} from "@mui/material";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import GridViewIcon from "@mui/icons-material/GridView";
import ViewListIcon from "@mui/icons-material/ViewList";
import SortIcon from "@mui/icons-material/Sort";
import AssessmentIcon from "@mui/icons-material/Assessment";
import BusinessIcon from "@mui/icons-material/Business";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import DeleteIcon from "@mui/icons-material/Delete";
import ComponentLoading from "../App/ComponentLoading";
import { AuthContext } from "../../contexts/AuthContext";
import { useErrorDialog } from "../../contexts/ErrorDialogContext";
import { useSuccessDialog } from "../../contexts/SuccessDialogContext";
import RecycleBinCompanyCard from "./RecycleBin/RecycleBinCompanyCard";
import ConfirmationDialog from "./RecycleBin/ConfirmationDialog";

const RecycleBin = () => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [companyToRestore, setCompanyToRestore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortType, setSortType] = useState("financialYearEnd");
  const [displayMode, setDisplayMode] = useState(() => {
    try {
      const saved = localStorage.getItem("recycleBin.displayMode");
      return saved === "list" || saved === "card" ? saved : "card";
    } catch {
      return "card";
    }
  });

  const { getCompanyList, restoreCompany, deleteCompany } =
    useContext(AuthContext);
  const showError = useErrorDialog();
  const showSuccess = useSuccessDialog();

  useEffect(() => {
    try {
      localStorage.setItem("recycleBin.displayMode", displayMode);
    } catch {}
  }, [displayMode]);

  useEffect(() => {
    fetchCompanies();
  }, [getCompanyList]);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const companyList = await getCompanyList("inactive");
      setCompanies(companyList);
    } catch (error) {
      showError(error);
    }
    setLoading(false);
  };

  const handleRestoreCompany = async () => {
    setRestoreLoading(true);
    try {
      await restoreCompany({ c_id: companyToRestore.c_id });
      showSuccess("Company successfully restored");
      const updatedCompanies = await getCompanyList("inactive");
      setCompanies(updatedCompanies);
      setRestoreDialogOpen(false);
      setCompanyToRestore(null);
    } catch (error) {
      showError(error);
    } finally {
      setRestoreLoading(false);
    }
  };

  const handleDeleteCompany = async () => {
    setDeleteLoading(true);
    try {
      await deleteCompany({ c_id: companyToDelete.c_id });
      showSuccess("Company permanently deleted");
      const updatedCompanies = await getCompanyList("inactive");
      setCompanies(updatedCompanies);
      setDeleteDialogOpen(false);
      setCompanyToDelete(null);
    } catch (error) {
      showError(error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleRestoreClick = (company) => {
    setCompanyToRestore(company);
    setRestoreDialogOpen(true);
  };

  const handleDeleteClick = (company) => {
    setCompanyToDelete(company);
    setDeleteDialogOpen(true);
  };

  const handleSortChange = (event) => {
    setSortType(event.target.value);
  };

  const sortedAndFilteredCompanies = companies
    .filter((company) => {
      const matchesSearch =
        (company.name &&
          company.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (company.uen &&
          company.uen.toLowerCase().includes(searchTerm.toLowerCase()));
      return matchesSearch;
    })
    .sort((a, b) => {
      if (sortType === "financialYearEnd") {
        const dateA = new Date(a.financialYearEnd || 0);
        const dateB = new Date(b.financialYearEnd || 0);
        if (dateA.getMonth() !== dateB.getMonth()) {
          return dateA.getMonth() - dateB.getMonth();
        }
        return dateA.getFullYear() - dateB.getFullYear();
      }
      if (sortType === "alphabetical") {
        return (a.name || "").localeCompare(b.name || "");
      }
      return 0;
    });

  if (loading) {
    return <ComponentLoading />;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "start",
        height: "100%",
        bgcolor: "grey.50",
      }}
    >
      <Box
        sx={{
          width: "100%",
          background: "rgba(255, 255, 255, 0.98)",
          backdropFilter: "blur(12px)",
          px: { xs: 2.5, sm: 3 },
          py: { xs: 3, md: 3.25 },
          mb: 2,
          boxShadow: "0 2px 16px rgba(0, 0, 0, 0.06)",
          borderBottom: "1px solid",
          borderColor: (theme) => alpha(theme.palette.primary.main, 0.25),
          position: "relative",
          overflow: "visible",
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: -1,
            left: 0,
            right: 0,
            height: "2px",
            background:
              "linear-gradient(90deg, transparent, #603F97 30%, #D31D83 70%, transparent)",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            gap: { xs: 1.5, sm: 2 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexShrink: 0,
              py: { xs: 0.25, sm: 0 },
              mr: 3,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "12px",
                background: "linear-gradient(135deg, #603F97 0%, #D31D83 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                boxShadow: "0 4px 10px rgba(96, 63, 151, 0.25)",
              }}
            >
              <DeleteForeverIcon sx={{ color: "white", fontSize: 22 }} />
            </Box>
            <Box>
              <Typography
                variant='h5'
                sx={{
                  fontWeight: 700,
                  background:
                    "linear-gradient(45deg, #603F97 30%, #D31D83 90%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  fontSize: { xs: "1.35rem", sm: "1.5rem", md: "1.625rem" },
                  lineHeight: 1.2,
                }}
              >
                Recycle Bin
              </Typography>
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ mt: 0.25 }}
              >
                {companies.length} deleted companies
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 1.25, sm: 1.5 },
              alignItems: { xs: "stretch", sm: "center" },
              minWidth: 0,
              flex: { sm: 1 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 1.25,
                minWidth: 0,
                flex: 1,
              }}
            >
              <TextField
                variant='outlined'
                size='small'
                placeholder='Search deleted companies...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  sx: { "& input": { py: 0.875, fontSize: "0.875rem" } },
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcon sx={{ color: "grey.500", fontSize: 18 }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment
                      position='end'
                      sx={{ cursor: "pointer" }}
                      onClick={() => setSearchTerm("")}
                    >
                      <Tooltip title='Clear search' arrow>
                        <Box sx={{ p: 0.25 }}>
                          <CloseIcon
                            sx={{
                              color: "grey.400",
                              fontSize: 16,
                              "&:hover": { color: "primary.main" },
                            }}
                          />
                        </Box>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: "340px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                    backgroundColor: "white",
                    pr: 1,
                    "&:hover": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "primary.main",
                      },
                    },
                    "&.Mui-focused": {
                      boxShadow: (theme) =>
                        `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
                      borderColor: "primary.main",
                    },
                  },
                }}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 1.25,
                flexWrap: "wrap",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <FormControl size='small' sx={{ minWidth: 120 }}>
                <InputLabel sx={{ fontSize: "0.8125rem" }}>Sort</InputLabel>
                <Select
                  value={sortType}
                  onChange={handleSortChange}
                  size='small'
                  label='Sort'
                  sx={{
                    borderRadius: 1.5,
                    backgroundColor: "white",
                    fontSize: "0.8125rem",
                    "& .MuiSelect-select": { py: 0.875 },
                    "&:hover": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "primary.main",
                      },
                    },
                    "&.Mui-focused": {
                      boxShadow: (theme) =>
                        `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
                    },
                  }}
                >
                  <MenuItem value='financialYearEnd'>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                    >
                      <AssessmentIcon sx={{ fontSize: 18, mr: 0.75 }} />
                      FY End
                    </Box>
                  </MenuItem>
                  <MenuItem value='alphabetical'>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                    >
                      <SortIcon sx={{ fontSize: 18, mr: 0.75 }} />
                      Name (A-Z)
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>

              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Tooltip title='Card View' arrow>
                  <ToggleButton
                    value='card'
                    selected={displayMode === "card"}
                    onChange={() => setDisplayMode("card")}
                    sx={{
                      borderRadius: 1.25,
                      p: 0.625,
                      minWidth: "auto",
                      "&.Mui-selected": {
                        backgroundColor: (theme) =>
                          alpha(theme.palette.primary.main, 0.12),
                        color: "primary.main",
                        boxShadow: (theme) =>
                          `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`,
                      },
                      "&:hover": {
                        backgroundColor: (theme) =>
                          alpha(theme.palette.grey[200], 0.6),
                      },
                    }}
                    aria-label='Card view'
                  >
                    <GridViewIcon sx={{ fontSize: 18 }} />
                  </ToggleButton>
                </Tooltip>
                <Tooltip title='List View' arrow>
                  <ToggleButton
                    value='list'
                    selected={displayMode === "list"}
                    onChange={() => setDisplayMode("list")}
                    sx={{
                      borderRadius: 1.25,
                      p: 0.625,
                      minWidth: "auto",
                      "&.Mui-selected": {
                        backgroundColor: (theme) =>
                          alpha(theme.palette.primary.main, 0.12),
                        color: "primary.main",
                        boxShadow: (theme) =>
                          `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`,
                      },
                      "&:hover": {
                        backgroundColor: (theme) =>
                          alpha(theme.palette.grey[200], 0.6),
                      },
                    }}
                    aria-label='List view'
                  >
                    <ViewListIcon sx={{ fontSize: 18 }} />
                  </ToggleButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {companies.length === 0 ? (
        <Fade in timeout={320}>
          <Box
            sx={{
              width: "100%",
              px: 3,
              py: 4,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 280,
            }}
          >
            <Paper
              variant='outlined'
              sx={{
                p: 4,
                borderRadius: 2,
                textAlign: "center",
                maxWidth: 420,
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
                bgcolor: "background.paper",
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                }}
              >
                <DeleteForeverIcon
                  sx={{ fontSize: 32, color: "primary.main" }}
                />
              </Box>
              <Typography variant='h6' fontWeight={600} gutterBottom>
                Recycle bin is empty
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                No deleted companies. Items you move to the recycle bin will
                appear here and can be restored within 30 days.
              </Typography>
            </Paper>
          </Box>
        </Fade>
      ) : sortedAndFilteredCompanies.length === 0 ? (
        <Fade in timeout={320}>
          <Box
            sx={{
              width: "100%",
              px: 3,
              py: 4,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 200,
            }}
          >
            <Typography variant='body1' color='text.secondary'>
              No companies match your search.
            </Typography>
          </Box>
        </Fade>
      ) : displayMode === "card" ? (
        <Fade in key='card' timeout={320}>
          <Box sx={{ width: "100%" }}>
            <Grid container spacing={2.5} sx={{ px: 3, py: 2 }}>
              {sortedAndFilteredCompanies.map((company, index) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={3}
                  lg={2.4}
                  key={company.c_id || index}
                >
                  <RecycleBinCompanyCard
                    company={company}
                    onRestore={handleRestoreClick}
                    onDelete={handleDeleteClick}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Fade>
      ) : (
        <Fade in key='list' timeout={320}>
          <Box sx={{ width: "100%" }}>
            <Box sx={{ px: 3, py: 2, width: "100%" }}>
              <TableContainer
                component={Paper}
                variant='outlined'
                sx={{ borderRadius: 2, overflow: "hidden" }}
              >
                <Table size='medium'>
                  <TableHead>
                    <TableRow
                      sx={{
                        bgcolor: (theme) =>
                          alpha(theme.palette.primary.main, 0.06),
                      }}
                    >
                      <TableCell
                        sx={{ fontWeight: 600, py: 1, fontSize: "0.8125rem" }}
                      >
                        Company
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, py: 1, fontSize: "0.8125rem" }}
                      >
                        UEN
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, py: 1, fontSize: "0.8125rem" }}
                      >
                        YA
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, py: 1, fontSize: "0.8125rem" }}
                      >
                        FY End
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, py: 1, fontSize: "0.8125rem" }}
                      >
                        Expires
                      </TableCell>
                      <TableCell
                        align='right'
                        sx={{
                          fontWeight: 600,
                          py: 1,
                          fontSize: "0.8125rem",
                          width: 120,
                        }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedAndFilteredCompanies.map((company, index) => {
                      const deletedAt = company.deleted_at
                        ? new Date(company.deleted_at)
                        : null;
                      const expiresAt =
                        deletedAt && !isNaN(deletedAt.getTime())
                          ? new Date(
                              deletedAt.getTime() + 30 * 24 * 60 * 60 * 1000
                            ).toLocaleDateString("en-GB", {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            })
                          : "—";
                      return (
                        <TableRow
                          key={company.c_id || index}
                          hover
                          sx={{
                            "&:hover": {
                              bgcolor: (theme) =>
                                alpha(theme.palette.primary.main, 0.04),
                            },
                          }}
                        >
                          <TableCell sx={{ py: 0.75, fontSize: "0.875rem" }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 36,
                                  height: 36,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  borderRadius: 1.5,
                                  bgcolor: (theme) =>
                                    alpha(theme.palette.primary.main, 0.08),
                                }}
                              >
                                <BusinessIcon
                                  color='primary'
                                  fontSize='small'
                                />
                              </Box>
                              <Typography fontWeight={500} noWrap>
                                {company.name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ py: 0.75, fontSize: "0.875rem" }}>
                            <Chip
                              label={company.uen}
                              size='small'
                              variant='outlined'
                              sx={{
                                bgcolor: (theme) =>
                                  alpha(theme.palette.primary.main, 0.08),
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 0.75, fontSize: "0.875rem" }}>
                            YA {company.yearOfAssessment ?? "—"}
                          </TableCell>
                          <TableCell
                            sx={{
                              color: "text.secondary",
                              py: 0.75,
                              fontSize: "0.875rem",
                            }}
                          >
                            {company.financialYearEnd
                              ? new Date(
                                  company.financialYearEnd
                                ).toLocaleString("default", {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                })
                              : "N/A"}
                          </TableCell>
                          <TableCell
                            sx={{
                              color: "error.main",
                              py: 0.75,
                              fontSize: "0.875rem",
                              fontWeight: 500,
                            }}
                          >
                            {expiresAt}
                          </TableCell>
                          <TableCell
                            align='right'
                            sx={{ py: 0.75 }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                gap: 0.25,
                                justifyContent: "flex-end",
                              }}
                            >
                              <Tooltip title='Restore Company' arrow>
                                <IconButton
                                  size='small'
                                  onClick={() => handleRestoreClick(company)}
                                  sx={{
                                    "&:hover": {
                                      bgcolor: (theme) =>
                                        alpha(theme.palette.success.main, 0.08),
                                      color: "success.main",
                                    },
                                  }}
                                >
                                  <RestoreFromTrashIcon
                                    fontSize='small'
                                    color='success.main'
                                  />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title='Delete permanently' arrow>
                                <IconButton
                                  size='small'
                                  onClick={() => handleDeleteClick(company)}
                                  sx={{
                                    "&:hover": {
                                      bgcolor: (theme) =>
                                        alpha(theme.palette.error.main, 0.08),
                                      color: "error.main",
                                    },
                                  }}
                                >
                                  <DeleteIcon fontSize='small' />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        </Fade>
      )}

      <ConfirmationDialog
        open={restoreDialogOpen}
        onClose={() => setRestoreDialogOpen(false)}
        onConfirm={handleRestoreCompany}
        title='Restore Company'
        message={`Are you sure you want to restore ${companyToRestore?.name}?`}
        confirmText='Restore'
        loading={restoreLoading}
        confirmColor='success'
      />

      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteCompany}
        title='Permanently Delete Company'
        message={`Are you sure you want to permanently delete ${companyToDelete?.name}? This action cannot be undone.`}
        confirmText='Delete'
        loading={deleteLoading}
        confirmColor='error'
      />
    </Box>
  );
};

export default RecycleBin;
